/**
 * @file CustomerHeaderActions.tsx
 * @description Видимые кнопки шапки карточки клиента (phase19.31, SC67,
 * SC68): «+ Заказ», «Написать», «Редактировать», «+ Задача» и последней —
 * «Анонимизировать» в опасном тоне, с прежним подтверждением. Без «⋯».
 *
 * Имя клиента важнее кнопок (приёмка QA-5 D3: на 1024 имя пропадало, на 1280
 * «Пётр Д…»). Кнопки разложены на три части (`part`) для `CabinetPageHeader`:
 *  - `main` — «+ Заказ», главное действие (`action`): в панели от 1024, на
 *    телефоне и планшете — в нижней полосе;
 *  - `panel` — «Написать», «Редактировать» (`panelActions`): в панели только
 *    от 1280, уже — строкой под шапкой;
 *  - `row` — «+ Задача», «Анонимизировать» (`secondaryActions` +
 *    `secondaryBelow`): всегда строкой под шапкой.
 *
 * Каждая кнопка — по праву: нет права — нет кнопки (а не серая).
 * «+ Заказ» открывает «Новый заказ» с подставленным клиентом (`?customerId=`).
 */

import { MessageSquare, Pencil, Plus, UserX } from "lucide-react";
import { Link } from "react-router";

import { Button } from "../../ui/button";

/** Часть шапки, которую рисует компонент. */
export type CustomerHeaderPart = "main" | "panel" | "row";

interface Props {
  part: CustomerHeaderPart;
  /** Адрес переписки; нет — кнопки «Написать» нет. */
  chatHref?: string;
  /** Адрес «Нового заказа» с клиентом; нет — кнопки нет. */
  newOrderHref?: string;
  onAddTask?: () => void;
  onEdit?: () => void;
  onAnonymize?: () => void;
  anonymizing?: boolean;
}

export function CustomerHeaderActions(props: Props) {
  const { part, chatHref, newOrderHref, onAddTask, onEdit, onAnonymize, anonymizing = false } = props;
  if (part === "main") {
    return newOrderHref ? (
      <Button variant="brand" asChild>
        <Link to={newOrderHref}>
          <Plus className="size-4" aria-hidden />
          Заказ
        </Link>
      </Button>
    ) : null;
  }
  if (part === "panel") {
    return (
      <>
        {chatHref ? (
          <Button variant="outline" asChild>
            <Link to={chatHref}>
              <MessageSquare className="size-4" aria-hidden />
              Написать
            </Link>
          </Button>
        ) : null}
        {onEdit ? (
          <Button variant="outline" onClick={onEdit}>
            <Pencil className="size-4" aria-hidden />
            Редактировать
          </Button>
        ) : null}
      </>
    );
  }
  return (
    <>
      {onAddTask ? (
        <Button variant="outline" onClick={onAddTask}>
          <Plus className="size-4" aria-hidden />
          Задача
        </Button>
      ) : null}
      {onAnonymize ? (
        <Button variant="destructive" onClick={onAnonymize} loading={anonymizing}>
          <UserX className="size-4" aria-hidden />
          Анонимизировать
        </Button>
      ) : null}
    </>
  );
}
